'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React from 'react';

import cx from 'classnames';
import { AnimatePresence, m } from 'framer-motion';
import NextImage from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';

import FavoriteButton from 'components/globals/FavoriteButton';

import BlockCallout from 'components/blocks/BlockCallout/BlockCallout';
import Accordion, { Child as AccordionChild } from 'components/ui/Accordion';
import Award from 'components/ui/Award';
import ButtonCTA from 'components/ui/ButtonCTA';
import DestiniBuy from 'components/ui/DestiniBuyButton';
import Grid from 'components/ui/Grid';
import Img from 'components/ui/Img';
import SvgIcon from 'components/ui/SvgIcon';
import Text from 'components/ui/Text';

import { useTextAutoContrast } from 'hooks/useTextAutoContrast';
import { getColor } from 'utils';
import { addProtocol } from 'utils/url';

import Ingredients from './Ingredients';
import Nutrition from './Nutrition';

import styles from './Product.module.scss';

const iconMap = {
    New: 'newPdpRibbonDark',
};

const Product = ({
    id,
    blocks,
    icon,
    allergens,
    award,
    awardAsset,
    callout,
    calloutEyebrow,
    discontinued,
    textColor,
    color,
    displayName,
    eyebrow,
    image,
    ingredients,
    nutritionInfo,
    productGroup,
    hideWhereToBuyButton,
    sizes,
    slug,
    theme,
    relatedPages,
}) => {
    blocks ??= null;
    icon ??= '';
    textColor ??= 'Auto';

    let isDark = useTextAutoContrast(color) === 'dark';
    if (textColor === 'Light') {
        isDark = false;
    } else if (textColor === 'Dark') {
        isDark = true;
    }

    return (
        <div className={cx(styles.productPage, styles[`theme--${theme}`])}>
            <div
                className={cx(styles.header, {
                    [styles['header--dark-text']]:
                        isDark || theme === 'makers-reserve',
                })}
                style={{
                    '--product-background-color': getColor(color),
                }}
            >
                <Grid className={styles.headerInner}>
                    {iconMap[icon] && (
                        <div className={styles.iconPlacement}>
                            <div className={styles.icon}>
                                <SvgIcon type={iconMap[icon]} />
                            </div>
                        </div>
                    )}
                    <div className={styles.productImage}>
                        <AnimatePresence>
                            {image?.url && (
                                <m.div
                                    className={cx(
                                        styles.imageContainer,
                                        award && styles.hasAward
                                    )}
                                    key={slug}
                                    initial={{
                                        opacity: 0,
                                        y: '5%',
                                    }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                    }}
                                    exit={{
                                        opacity: 0,
                                        y: '-5%',
                                    }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <figure className={styles.image}>
                                        <div className={styles.imageInner}>
                                            <NextImage
                                                width={image?.width}
                                                height={image?.height}
                                                src={addProtocol(image.url)}
                                                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw"
                                                alt={
                                                    image.description ||
                                                    displayName ||
                                                    ''
                                                }
                                                priority
                                            />
                                            {theme !== 'makers-reserve' && (
                                                <m.div
                                                    className={
                                                        styles.imageShadow
                                                    }
                                                    key={slug}
                                                    initial={{
                                                        opacity: 0,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        transition: {
                                                            duration: 0.3,
                                                            delay: 0.3,
                                                        },
                                                    }}
                                                    exit={{
                                                        opacity: 0,
                                                    }}
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </div>
                                    </figure>
                                </m.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <div
                        className={cx(styles.titleContainer, {
                            [styles.hasAward]: award || awardAsset?.url,
                        })}
                    >
                        {!award && eyebrow && (
                            <div className={styles.eyebrow}>
                                <Text.Theme
                                    as="h3"
                                    config={{
                                        'makers-reserve': {
                                            baseTheme: 'labelSmallAlt',
                                            themes: {
                                                large: 'labelMediumAlt',
                                            },
                                        },
                                        default: {
                                            baseTheme: 'labelSmall',
                                            themes: {
                                                large: 'labelLarge',
                                            },
                                        },
                                    }}
                                >
                                    {eyebrow}
                                </Text.Theme>
                            </div>
                        )}
                        {(award?.json || awardAsset?.url) && (
                            <div className={styles.awardWrapper}>
                                {!awardAsset?.url && (
                                    <div className={cx(styles.award)}>
                                        <Award
                                            richText={award}
                                            isDark={isDark}
                                        />
                                    </div>
                                )}
                                {awardAsset?.url && (
                                    <img
                                        className={cx(styles.awardAsset)}
                                        src={awardAsset.url}
                                        alt=""
                                    />
                                )}
                            </div>
                        )}
                        <div className={styles.title}>
                            <Text.Theme
                                as="h1"
                                config={{
                                    'makers-reserve': {
                                        baseTheme: 'makers36',
                                        themes: {
                                            medium: 'makers60',
                                            large: 'parkinsonXLarge',
                                        },
                                    },
                                    default: {
                                        baseTheme: 'displaySmall',
                                        themes: {
                                            medium: 'displayMedium',
                                            xLarge: 'displayLarge',
                                            xxxLarge: 'displayXLarge',
                                        },
                                    },
                                }}
                            >
                                {displayName}
                            </Text.Theme>
                        </div>

                        {discontinued ? (
                            <div className={styles.discontinued}>
                                <SvgIcon type="caution" />
                                <Text baseTheme="bodySmall">
                                    This product is no longer available
                                </Text>
                            </div>
                        ) : sizes ? (
                            <div className={styles.cta}>
                                <div className={styles.ctaButtons}>
                                    {!hideWhereToBuyButton && (
                                        <DestiniBuy
                                            sku={sizes.map(item => item.sku)}
                                            button={
                                                <ButtonCTA
                                                    text="Where to Buy"
                                                    iconType="location"
                                                    style={
                                                        {
                                                            'makers-reserve':
                                                                'makersFilled-brown-gold',
                                                            default: isDark
                                                                ? 'filled-blue-cream'
                                                                : 'filled-cream-white',
                                                        }[theme ?? 'default']
                                                    }
                                                />
                                            }
                                        />
                                    )}
                                    <FavoriteButton
                                        itemType="product"
                                        itemId={id}
                                        itemTitle={displayName}
                                        itemSlug={slug}
                                        itemImage={image?.url}
                                        showCount={true}
                                        realtime={true}
                                        size="large"
                                    />
                                </div>

                                <div className={styles.weights}>
                                    <Text
                                        baseTheme="labelSmall"
                                        themes={{ medium: 'labelLarge' }}
                                    >
                                        {sizes
                                            .map(size => size.size)
                                            .join(' / ')}
                                    </Text>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </Grid>
            </div>
            {productGroup?.products?.items && (
                <div className={styles.productVariantsContainer}>
                    <ul className={styles.productVariants}>
                        {productGroup.products.items.map((product, i) => {
                            return (
                                <li key={i}>
                                    <Link
                                        href={`/products/${product.category?.slug}/${product.slug}`}
                                        scroll={false}
                                        className={cx(styles.productVariant, {
                                            [styles[
                                                'productVariant--selected'
                                            ]]: slug == product.slug,
                                        })}
                                    >
                                        <Img
                                            src={
                                                product.groupThumbnail?.url ||
                                                product.image?.url
                                            }
                                            width={
                                                product.groupThumbnail?.width ||
                                                product.image?.width
                                            }
                                            height={
                                                product.groupThumbnail
                                                    ?.height ||
                                                product.image?.height
                                            }
                                            fallbackImageWidth={150}
                                            loading="eager"
                                            alt={displayName}
                                        />
                                        <Text
                                            baseTheme="labelSmall"
                                            themes={{
                                                medium: 'labelLarge',
                                            }}
                                            className={
                                                styles['productVariant__title']
                                            }
                                        >
                                            {product.groupLabel}
                                        </Text>
                                        {slug == product.slug && (
                                            <div
                                                className={
                                                    styles.productVariantIconChecked
                                                }
                                                aria-hidden="true"
                                                role="presentation"
                                            >
                                                <SvgIcon type="roundCheckAlt" />
                                            </div>
                                        )}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
            <div className={styles.content}>
                {callout && (
                    <BlockCallout
                        hasNotch
                        theme="Banner"
                        eyebrow={calloutEyebrow || displayName}
                        roundedCorners={false}
                        richHeadline={{
                            json: {
                                nodeType: 'document',
                                content: [
                                    {
                                        nodeType: 'paragraph',
                                        content: [
                                            {
                                                nodeType: 'text',
                                                value: `${callout}`,
                                                marks: [],
                                            },
                                        ],
                                    },
                                ],
                            },
                        }}
                        colorTheme={theme}
                        __typename="blockCallout"
                    />
                )}

                <Accordion className={styles.accordion}>
                    {nutritionInfo && (
                        <AccordionChild label={'Nutrition & Allergens'}>
                            <Nutrition
                                nutritionInfo={nutritionInfo}
                                allergens={allergens}
                            />
                        </AccordionChild>
                    )}
                    <AccordionChild label={'Ingredients'}>
                        <Ingredients ingredients={ingredients} />
                    </AccordionChild>
                </Accordion>
            </div>
            <div className={styles.blocks}>{blocks}</div>
            {relatedPages}
        </div>
    );
};

Product.propTypes = {
    displayName: PropTypes.string.isRequired,
    pageTitle: PropTypes.string.isRequired,
    discontinued: PropTypes.bool,
    shareImage: PropTypes.object,
    eyebrow: PropTypes.string.isRequired,
    pageDescription: PropTypes.string.isRequired,
    category: PropTypes.object.isRequired,
    slug: PropTypes.string.isRequired,
    productGroup: PropTypes.object,
    textColor: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    image: PropTypes.object,
    calloutEyebrow: PropTypes.string,
    callout: PropTypes.string,
    nutritionInfo: PropTypes.array.isRequired,
    allergens: PropTypes.string,
    award: PropTypes.object,
    awardAsset: PropTypes.object,
    hideWhereToBuyButton: PropTypes.bool,
    icon: PropTypes.string,
    sizes: PropTypes.array,
    sizes3: PropTypes.array,
    groupThumbnail: PropTypes.object,
    groupLabel: PropTypes.string,
    ingredients: PropTypes.array,
    tillamapsUrl: PropTypes.string,
    shopProduct: PropTypes.object,
    theme: PropTypes.string,
    seoTitle: PropTypes.string,
    shareTitle: PropTypes.string,
    metaDescription: PropTypes.string,
    shareDescription: PropTypes.string,
    relatedPages: PropTypes.object,
    blocks: PropTypes.array,
};

export default Product;
